/**
 * Deck design tokens — sized to the 1920×1080 fixed slide frame so every
 * BlockSlide reads at the same scale as the Prairies / CNE pitch decks
 * (see src/app/sales/canadian-national-exhibition/page.tsx for the canonical
 * stack). Pair with `text-*` colour utilities at the call site.
 */

export const EYEBROW = 'font-dank-mono-italic text-lg tracking-widest leading-snug';
export const EYEBROW_SM =
  'font-dank-mono-italic text-base tracking-[0.14em] leading-snug md:text-lg';

export const H1_COVER =
  'font-gelica text-5xl font-extralight leading-[1.08] tracking-[-0.02em] sm:text-6xl md:text-7xl lg:text-8xl';
export const H1 =
  'font-gelica text-5xl font-extralight leading-[1.08] tracking-[-0.02em] sm:text-6xl md:text-7xl lg:text-8xl';
export const H2 =
  'font-gelica text-4xl font-extralight leading-[1.1] tracking-[-0.02em] md:text-5xl';
export const QUOTE =
  'font-gelica text-3xl font-extralight italic leading-snug sm:text-4xl md:text-5xl lg:text-6xl';
export const QUOTE_SM = 'font-gelica text-2xl font-extralight italic leading-snug md:text-3xl';
export const PRICE =
  'font-gelica text-5xl font-extralight italic leading-none tracking-tight md:text-6xl';

export const LEDE = 'font-inter text-xl leading-snug md:text-2xl';
/** Block canvas (1920×1080): Marv-scale supporting copy — larger than legacy sales tokens */
export const LEDE_DECK =
  'font-inter text-xl leading-snug sm:text-2xl md:text-[1.65rem] md:leading-snug lg:text-[1.85rem]';
export const BODY_LG = 'font-inter text-lg leading-relaxed md:text-xl';
export const BODY = 'font-inter text-lg leading-relaxed md:text-xl';
/** Block canvas body + list items — reads ~21–24px Marv equivalent scaled to full HD; looser leading for multi-line paragraphs */
export const BODY_DECK =
  'font-inter text-lg leading-[1.65] sm:text-xl sm:leading-[1.68] md:text-[1.35rem] md:leading-[1.72] lg:text-[1.45rem] lg:leading-[1.78]';
export const BODY_DIM = 'font-inter text-lg leading-snug md:text-xl md:leading-relaxed';
export const BODY_SM = 'font-inter text-sm leading-snug md:text-base';
export const CAPTION = 'font-inter text-xs leading-snug md:text-sm';
export const CARD_TITLE = 'font-inter text-lg font-bold';

export const META = 'font-dank-mono text-[11px] tracking-[0.12em] leading-snug md:text-[12px]';
export const BADGE = 'font-dank-mono text-[10px] tracking-[0.12em]';

export const ACCENT = 'font-dank-mono-italic';

// ── Deck list bullets (Crow: SOUL.md bird glyphs — ∿ emphasis trail; no arrows)
/** Unicode sine-wave / squiggle used as list mark in deck UIs */
export const DECK_BULLET_GLYPH = '\u223f';
/** Bounded measure, vertical rhythm (Bringhurst ~65ch; Zapf-style leading) */
export const DECK_BULLET_LIST =
  'list-none space-y-6 pl-0 max-w-[65ch] text-left antialiased';
/** Marker column + text column — scan-friendly ragged right */
export const DECK_BULLET_ROW =
  'grid grid-cols-[1.125rem_minmax(0,1fr)] gap-x-3.5 items-start';
/** Optical alignment to first line — matches list text colour (inherits from row / ul) */
export const DECK_BULLET_MARKER =
  'select-none justify-self-center font-inter text-[0.92em] font-normal leading-none text-current pt-[0.2em]';
/** Text column in bullet rows — min-w-0 so grid/flex children wrap (pairs with DECK_BULLET_ROW) */
export const DECK_BULLET_TEXT = 'min-w-0';
/** Tighter vertical rhythm for short feature lines (e.g. pricing tiers) */
export const DECK_BULLET_LIST_TIGHT =
  'list-none space-y-2.5 pl-0 max-w-[55ch] text-left antialiased';

export const CARD = 'rounded-2xl border border-[#2E3245]/20 bg-white p-5 md:p-6';

export const PAD = 'px-6 py-10 sm:px-12 sm:py-12 md:px-16 md:py-14';
export const INNER = 'mx-auto w-full max-w-7xl';

/** Returns the object-position utility for an image given the slide bg's position. */
export function objectPositionClass(position?: string): string {
  switch (position) {
    case 'left':
      return 'object-left';
    case 'right':
      return 'object-right';
    case 'top':
      return 'object-top';
    case 'bottom':
    case 'object-bottom':
      return 'object-bottom';
    case 'bottom-center':
      return 'object-bottom';
    case 'center':
    default:
      return 'object-center';
  }
}
